import React from "react";
import DatePicker from "../../../../components/DatePicker";
import DateRangePicker from "../../../../components/DateRangePicker";
import ReusableDropdown from "../../../../components/ReusableDropdown";
import ScopedEntityFilter from "./ScopedEntityFilter";

const inputStyle = (isDark, isRTL) => ({
  width: "100%", padding: "9px 12px", borderRadius: 10,
  border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
  background: isDark ? "rgba(255,255,255,0.05)" : "#fff",
  color: isDark ? "#f1f5f9" : "#1e293b", fontSize: 13, outline: "none",
  textAlign: isRTL ? "right" : "left", boxSizing: "border-box",
});

const labelStyle = (isDark, isRTL) => ({
  display: "block", fontSize: 11.5, fontWeight: 700, marginBottom: 6,
  color: isDark ? "rgba(255,255,255,0.55)" : "rgba(30,41,59,0.55)",
  textTransform: "uppercase", letterSpacing: 0.3, textAlign: isRTL ? "right" : "left",
});

const ENUM_OPTIONS = {
  attendanceStatus: ["present", "absent", "late"],
  leaveType: ["Annual", "Sick", "Emergency", "Maternity"],
  employmentStatus: ["Active", "OnLeave", "Inactive"],
};

// Fields backed by real, tenant-scoped data (see useReportFilterOptions) —
// rendered as a searchable ReusableDropdown, never free text.
const ENTITY_OPTION_SOURCE = {
  department: "departments",
  manager: "managers",
};

// employee / company get the fuller specific-vs-multiple-vs-all picker
// (ScopedEntityFilter) instead of a plain single-value dropdown.
const SCOPED_ENTITY_SOURCE = { employee: "employees", company: "companies" };

const dropdownInputStyle = (isDark, isRTL) => ({
  width: "100%", padding: "9px 12px", borderRadius: 10,
  border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
  background: isDark ? "rgba(255,255,255,0.05)" : "#fff",
  color: isDark ? "#f1f5f9" : "#1e293b", fontSize: 13,
  textAlign: isRTL ? "right" : "left", boxSizing: "border-box",
});

/** Renders one filter control by field key — text[language] provides labels. */
const FilterField = ({ fieldKey, value, onChange, theme, isRTL, text, language, options = {}, optionsLoading }) => {
  const isDark = theme === "dark";

  if (fieldKey === "dateMode") return null; // rendered inline by FiltersPanel alongside specificDate/dateRange

  if (fieldKey === "specificDate") {
    return (
      <div>
        <label style={labelStyle(isDark, isRTL)}>{text[language].filterSpecificDate}</label>
        <DatePicker theme={theme} isRTL={isRTL} value={value} onChange={onChange} />
      </div>
    );
  }

  if (fieldKey === "dateRange") {
    return (
      <div>
        <label style={labelStyle(isDark, isRTL)}>{text[language].filterDateRange}</label>
        <DateRangePicker
          value={value || {}}
          onChange={onChange}
          isRTL={isRTL}
          presetsLabels={{
            today: text[language].filterToday, thisWeek: text[language].filterThisWeek,
            thisMonth: text[language].filterThisMonth, thisQuarter: text[language].filterThisQuarter,
            from: text[language].filterFrom, to: text[language].filterTo,
          }}
        />
      </div>
    );
  }

  if (fieldKey === "payrollMonth") {
    return (
      <div>
        <label style={labelStyle(isDark, isRTL)}>{text[language].filterPayrollMonth}</label>
        <input type="month" value={value || ""} onChange={(e) => onChange(e.target.value)} style={inputStyle(isDark, isRTL)} />
      </div>
    );
  }

  if (ENUM_OPTIONS[fieldKey]) {
    const items = [{ key: "", label: text[language].filterAll }, ...ENUM_OPTIONS[fieldKey].map((opt) => ({ key: opt, label: opt }))];
    return (
      <div>
        <label style={labelStyle(isDark, isRTL)}>{text[language][`filter${fieldKey.charAt(0).toUpperCase()}${fieldKey.slice(1)}`]}</label>
        <ReusableDropdown
          items={items}
          value={value || ""}
          onChange={(key) => onChange(key)}
          getItemKey={(it) => it.key}
          getItemLabel={(it) => it.label}
          placeholder={text[language].filterAll}
          theme={theme}
          language={language}
          showAllOnArrowClick
          inputStyle={dropdownInputStyle(isDark, isRTL)}
        />
      </div>
    );
  }

  if (SCOPED_ENTITY_SOURCE[fieldKey]) {
    const list = options[SCOPED_ENTITY_SOURCE[fieldKey]] || [];
    const labelMap = { employee: "filterEmployee", company: "filterCompany" };
    return (
      <div>
        <label style={labelStyle(isDark, isRTL)}>{text[language][labelMap[fieldKey]]}</label>
        <ScopedEntityFilter
          value={value}
          onChange={onChange}
          list={list}
          theme={theme}
          isRTL={isRTL}
          language={language}
          text={text[language]}
          loading={optionsLoading}
        />
      </div>
    );
  }

  if (ENTITY_OPTION_SOURCE[fieldKey]) {
    const list = options[ENTITY_OPTION_SOURCE[fieldKey]] || [];
    const labelMap = { department: "filterDepartment", manager: "filterManager" };
    return (
      <div>
        <label style={labelStyle(isDark, isRTL)}>{text[language][labelMap[fieldKey]]}</label>
        <ReusableDropdown
          items={list}
          value={value || ""}
          onChange={(key) => onChange(key)}
          getItemKey={(it) => it.key}
          getItemLabel={(it) => it.label}
          placeholder={optionsLoading ? text[language].loading : text[language].filterAll}
          disabled={optionsLoading}
          theme={theme}
          language={language}
          maxItems={8}
          showAllOnArrowClick
          inputStyle={dropdownInputStyle(isDark, isRTL)}
        />
      </div>
    );
  }

  // search / shift / any not-yet-modeled field: free text is appropriate
  // here (it's a genuine keyword search, not a pick-from-a-list field).
  const labelMap = { search: "filterSearch", shift: "filterShift" };
  return (
    <div>
      <label style={labelStyle(isDark, isRTL)}>{text[language][labelMap[fieldKey]] || fieldKey}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} style={inputStyle(isDark, isRTL)} />
    </div>
  );
};

export default FilterField;
